import { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Input, Select, SelectItem, Spinner, Switch, type Selection } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { CtyButton } from '@circuitry-ai/doc-ui';
import {
  ROW_SCOPES,
  datasetObject,
  domainObject,
  useAdminClient,
  type AdminClient,
  type AdminGroup,
  type AdminUser,
  type Catalog,
  type Grant,
  type RowScope,
} from './api';
import { ErrorLine, SCOPE_LABELS, TabIntro, scopesLabel, useAdminLoad } from './shared';

/** The select's value for a cell that holds several scopes (shown, never chosen). */
const SEVERAL = '__several__';
const MAX_ALIASES = 20;
const MAX_ALIAS_LENGTH = 320;
const USERS_SHOWN = 50;

const cellKey = (object: string, group: string) => `${object}\n${group}`;

/** The grant's real scopes ('none' is no scope). */
const scopesOf = (grant: Grant | undefined): RowScope[] => (grant?.scopes ?? []).filter((scope) => scope !== 'none');
const grantsSomething = (grant: Grant | undefined) => scopesOf(grant).length > 0 || Boolean(grant?.sensitive);

/** `grants` with one cell replaced; a cell that grants nothing is dropped, as GET /grants leaves it out. */
function withGrant(grants: Record<string, Grant>, key: string, grant: Grant | undefined): Record<string, Grant> {
  const next = { ...grants };
  if (grant && grantsSomething(grant)) next[key] = grant;
  else delete next[key];
  return next;
}

interface MatrixData {
  catalog: Catalog;
  groups: AdminGroup[];
  grants: Record<string, Grant>;
}

/** The names picked in a multiple Select; nothing picked shows everything. */
const picked = (keys: Selection): ReadonlySet<string> => (keys === 'all' ? new Set() : new Set(Array.from(keys, String)));

/**
 * The matrix limited to the picked groups and domains. A picked name that is gone after a refresh
 * is ignored, and a filter left with no name that still exists shows everything.
 */
function filtered(data: MatrixData, groups: ReadonlySet<string>, domains: ReadonlySet<string>): MatrixData {
  const keptGroups = data.groups.filter((group) => groups.has(group.name));
  const keptDomains = data.catalog.domains.filter((domain) => domains.has(domain.domain));
  return {
    ...data,
    groups: keptGroups.length ? keptGroups : data.groups,
    catalog: { ...data.catalog, domains: keptDomains.length ? keptDomains : data.catalog.domains },
  };
}

/**
 * AI Access: which row scope each group (people and agents) has on each domain and dataset, and
 * whether it sees sensitive columns. A change saves that one cell. Below it, the identity aliases
 * the Own and Group row scopes match people by.
 */
export function AccessMatrixTab({ baseUrl }: { readonly baseUrl: string }) {
  const { t } = useTranslation();
  const client = useAdminClient(baseUrl);
  const { data, setData, loading, error, reload } = useAdminLoad<MatrixData>(client, async (c, signal) => {
    const [catalog, groups, grants] = await Promise.all([c.catalog(signal), c.groups(signal), c.grants(signal)]);
    // People groups first, then agent groups, each in the order the service lists them.
    const ordered = [...groups.filter((g) => g.kind !== 'agents'), ...groups.filter((g) => g.kind === 'agents')];
    const byCell: Record<string, Grant> = {};
    for (const grant of grants) byCell[cellKey(grant.object, grant.group)] = grant;
    return { catalog, groups: ordered, grants: byCell };
  });
  const [saving, setSaving] = useState<ReadonlySet<string>>(() => new Set());
  const [saveError, setSaveError] = useState<string | null>(null);
  const [groupFilter, setGroupFilter] = useState<ReadonlySet<string>>(() => new Set());
  const [domainFilter, setDomainFilter] = useState<ReadonlySet<string>>(() => new Set());
  const shown = useMemo(() => (data ? filtered(data, groupFilter, domainFilter) : null), [data, groupFilter, domainFilter]);

  const change = async (object: string, group: string, scope: RowScope, sensitive: boolean) => {
    if (!client || !data) return;
    const key = cellKey(object, group);
    const before = data.grants[key];
    const wanted: Grant = { object, group, scopes: scope === 'none' ? [] : [scope], sensitive: scope === 'none' ? false : sensitive };
    const put = (grant: Grant | undefined) => setData((prev) => (prev ? { ...prev, grants: withGrant(prev.grants, key, grant) } : prev));
    put(wanted);
    setSaveError(null);
    setSaving((prev) => new Set(prev).add(key));
    try {
      const result = await client.setGrant({ object, group, scope, sensitive: wanted.sensitive });
      put(result?.grant ?? wanted);
    } catch (e) {
      put(before);
      const name = object.slice(object.indexOf(':') + 1);
      setSaveError(t('Could not save {{group}} on {{name}}: {{message}}', { group, name, message: (e as Error).message }));
    } finally {
      setSaving((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  return (
    <div className="flex flex-col gap-4 px-6 pb-6">
      <TabIntro
        title={t('AI Access')}
        actions={(
          <CtyButton variant="flat" size="sm" onPress={() => void reload()} isDisabled={loading || !client}>
            {t('Refresh')}
          </CtyButton>
        )}
      >
        <span className="text-sm text-default-500">
          {t('Which rows each group may read through AI agents, per domain and dataset, and whether it sees sensitive columns. A dataset’s access is its own grant combined with its domain’s.')}
        </span>
      </TabIntro>

      {loading && <Spinner size="sm" className="self-start" />}
      <ErrorLine message={error} />
      <ErrorLine message={saveError} />
      {data && data.groups.length === 0 && <p className="text-sm text-default-500">{t('This tenant has no groups yet.')}</p>}
      {data && shown && data.groups.length > 0 && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <FilterSelect
              label={t('Groups')}
              placeholder={t('All groups')}
              selected={groupFilter}
              onChange={setGroupFilter}
              options={data.groups.map((group) => ({
                key: group.name,
                description: `${group.kind === 'agents' ? t('Agents') : t('People')} · ${t('{{count}} members', { count: group.members })}`,
              }))}
            />
            <FilterSelect
              label={t('Domains')}
              placeholder={t('All domains')}
              selected={domainFilter}
              onChange={setDomainFilter}
              options={data.catalog.domains.map((domain) => ({ key: domain.domain }))}
            />
            {(groupFilter.size > 0 || domainFilter.size > 0) && (
              <CtyButton variant="light" size="sm" onPress={() => { setGroupFilter(new Set()); setDomainFilter(new Set()); }}>
                {t('Clear filters')}
              </CtyButton>
            )}
          </div>
          <Matrix data={shown} saving={saving} onChange={change} />
        </>
      )}

      <AliasesSection client={client} />
    </div>
  );
}

/**
 * A filter that picks several names; none picked means all. Escape closes its list and keeps the
 * choice: react-aria's listbox would clear the selection on Escape instead, which here wipes the
 * filter of someone who only meant to close the list.
 */
function FilterSelect({ label, placeholder, selected, onChange, options }: {
  readonly label: string;
  readonly placeholder: string;
  readonly selected: ReadonlySet<string>;
  readonly onChange: (names: ReadonlySet<string>) => void;
  readonly options: readonly { key: string; description?: string }[];
}) {
  const [open, setOpen] = useState(false);
  return (
    // The list renders in a portal, but React still routes its key events through this wrapper.
    <div
      className="w-full max-w-xs"
      onKeyDownCapture={(event) => {
        if (open && event.key === 'Escape') {
          event.stopPropagation();
          setOpen(false);
        }
      }}
    >
      <Select
        label={label}
        placeholder={placeholder}
        selectionMode="multiple"
        size="sm"
        isOpen={open}
        onOpenChange={setOpen}
        selectedKeys={selected}
        onSelectionChange={(keys) => onChange(picked(keys))}
      >
        {options.map((option) => (
          <SelectItem key={option.key} value={option.key} textValue={option.key} description={option.description}>
            {option.key}
          </SelectItem>
        ))}
      </Select>
    </div>
  );
}

function Matrix({ data, saving, onChange }: {
  readonly data: MatrixData;
  readonly saving: ReadonlySet<string>;
  readonly onChange: (object: string, group: string, scope: RowScope, sensitive: boolean) => void;
}) {
  const { t } = useTranslation();
  const { catalog, groups, grants } = data;
  return (
    <div className="overflow-x-auto rounded-medium border border-default-200">
      <table className="w-full text-left text-sm" aria-label={t('AI access by group')}>
        <thead className="text-xs text-default-500">
          <tr>
            <th className="sticky left-0 z-20 bg-content1 px-3 py-2 font-medium">{t('Domain / dataset')}</th>
            {groups.map((group) => (
              <th key={group.name} className="min-w-[200px] px-3 py-2 align-bottom font-medium">
                <div className="flex flex-col">
                  <span className="text-foreground">{group.name}</span>
                  <span>
                    {group.kind === 'agents' ? t('Agents') : t('People')} · {t('{{count}} members', { count: group.members })}
                  </span>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {catalog.domains.flatMap((domain) => {
            const domainId = domainObject(domain.domain);
            const rows = [(
              <tr key={domainId} className="border-t border-default-200 bg-default-50">
                <th scope="row" className="sticky left-0 z-20 bg-default-50 px-3 py-2 font-semibold text-foreground" title={domain.description}>
                  {domain.domain}
                </th>
                {groups.map((group) => {
                  const key = cellKey(domainId, group.name);
                  return (
                    <td key={group.name} className="px-3 py-2 align-top">
                      <GrantCell
                        label={t('{{group}} on {{name}}', { group: group.name, name: domain.domain })}
                        grant={grants[key]}
                        options={ROW_SCOPES}
                        saving={saving.has(key)}
                        onChange={(scope, sensitive) => onChange(domainId, group.name, scope, sensitive)}
                      />
                    </td>
                  );
                })}
              </tr>
            )];
            for (const dataset of domain.datasets) {
              const datasetId = datasetObject(dataset.name);
              rows.push(
                <tr key={datasetId} className="border-t border-default-100">
                  <th scope="row" className="sticky left-0 z-20 bg-content1 py-2 pl-8 pr-3 font-mono text-xs font-normal" title={dataset.description}>
                    {dataset.name}
                  </th>
                  {groups.map((group) => {
                    const key = cellKey(datasetId, group.name);
                    const own = grants[key];
                    const fromDomain = grants[cellKey(domainId, group.name)];
                    return (
                      <td key={group.name} className="px-3 py-2 align-top">
                        <GrantCell
                          label={t('{{group}} on {{name}}', { group: group.name, name: dataset.name })}
                          grant={own}
                          options={dataset.scopes}
                          inherited={!grantsSomething(own) && grantsSomething(fromDomain) ? fromDomain : undefined}
                          saving={saving.has(key)}
                          onChange={(scope, sensitive) => onChange(datasetId, group.name, scope, sensitive)}
                        />
                      </td>
                    );
                  })}
                </tr>
              );
            }
            return rows;
          })}
        </tbody>
      </table>
    </div>
  );
}

/** One group × domain/dataset: its row scope, sensitive flag, and (dataset rows) what the domain grants. */
function GrantCell({ label, grant, options, inherited, saving, onChange }: {
  readonly label: string;
  readonly grant: Grant | undefined;
  readonly options: readonly RowScope[];
  readonly inherited?: Grant;
  readonly saving: boolean;
  readonly onChange: (scope: RowScope, sensitive: boolean) => void;
}) {
  const { t } = useTranslation();
  const switchName = useId();
  const scopes = scopesOf(grant);
  const several = scopes.length > 1;
  const value: string = several ? SEVERAL : scopes[0] ?? 'none';
  const sensitive = Boolean(grant?.sensitive);
  // 'No access' always; the scopes this row supports; and a held scope it no longer supports, so it still shows.
  const offered = ROW_SCOPES.filter((scope) => scope === 'none' || options.includes(scope) || scope === value);

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <select
          aria-label={t('Row scope for {{label}}', { label })}
          className="rounded-medium border border-default-200 bg-content1 px-2 py-1 text-xs"
          value={value}
          disabled={saving}
          onChange={(event) => onChange(event.target.value as RowScope, sensitive)}
        >
          {several && <option value={SEVERAL} disabled>{scopesLabel(scopes, t)}</option>}
          {offered.map((scope) => (
            <option key={scope} value={scope} disabled={scope !== 'none' && !options.includes(scope)}>
              {/* Under a domain grant, no grant of the cell's own still leaves the domain's rows. */}
              {scope === 'none' && inherited ? t('Domain only') : t(SCOPE_LABELS[scope])}
            </option>
          ))}
        </select>
        {/* NextUI names a switch by its visible label, so its full name comes from this hidden span. */}
        <span id={switchName} className="sr-only">{t('Sensitive for {{label}}', { label })}</span>
        <Switch
          size="sm"
          aria-labelledby={switchName}
          isSelected={sensitive}
          isDisabled={saving || value === 'none' || several}
          onValueChange={(next) => onChange(value as RowScope, next)}
          classNames={{ label: 'text-xs text-default-500' }}
        >
          {t('Sensitive')}
        </Switch>
        {saving && <Spinner size="sm" aria-label={t('Saving')} />}
      </div>
      {several && <span className="text-[11px] text-default-400">{t('Choosing a scope replaces these.')}</span>}
      {inherited && (
        <span className="text-[11px] text-default-400">
          {t('from domain: {{scope}}', { scope: scopesLabel(inherited.scopes, t) })}
          {inherited.sensitive ? `, ${t('sensitive')}` : ''}
        </span>
      )}
    </div>
  );
}

const sameList = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((value, i) => value === b[i]);

/** The tenant's members and their identity aliases, each editable and saved on its own. */
function AliasesSection({ client }: { readonly client: AdminClient | null }) {
  const { t } = useTranslation();
  const { data: users, setData, loading, error } = useAdminLoad<AdminUser[]>(client, (c, signal) => c.users(signal));
  const [query, setQuery] = useState('');
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users ?? [];
    return (users ?? []).filter((user) =>
      [user.name, user.email, user.user_id, ...user.aliases].some((value) => value?.toLowerCase().includes(q)));
  }, [users, query]);

  const saved = (userId: string, aliases: string[]) =>
    setData((prev) => prev?.map((user) => (user.user_id === userId ? { ...user, aliases } : user)) ?? prev);

  return (
    <section aria-label={t('Identity aliases')} className="flex flex-col gap-3 pt-2">
      <TabIntro title={t('Identity aliases')}>
        <span className="text-sm text-default-500">
          {t('Aliases are extra names a user appears under in owner columns (a username, a display name); the Own and Group row scopes use them to find that user’s rows.')}
        </span>
      </TabIntro>
      <Input
        aria-label={t('Search users')}
        placeholder={t('Search users')}
        size="sm"
        className="max-w-sm"
        value={query}
        onValueChange={setQuery}
        startContent={<Icon icon="lucide:search" width={16} />}
      />
      {loading && <Spinner size="sm" className="self-start" />}
      <ErrorLine message={error} />
      {users && filtered.length === 0 && <p className="text-sm text-default-500">{t('No users match your search.')}</p>}
      {filtered.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-xs text-default-500">
              <tr>
                <th className="py-1 pr-4 font-medium">{t('User')}</th>
                <th className="py-1 font-medium">{t('Aliases')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, USERS_SHOWN).map((user) => (
                <AliasRow key={user.user_id} user={user} client={client} onSaved={saved} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {filtered.length > USERS_SHOWN && (
        <p className="text-xs text-default-500">
          {t('Showing {{shown}} of {{total}} users. Search to find the others.', { shown: USERS_SHOWN, total: filtered.length })}
        </p>
      )}
    </section>
  );
}

function AliasRow({ user, client, onSaved }: {
  readonly user: AdminUser;
  readonly client: AdminClient | null;
  readonly onSaved: (userId: string, aliases: string[]) => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<string[]>(user.aliases);
  const [input, setInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const savedKey = user.aliases.join('\n');
  useEffect(() => {
    setDraft(savedKey ? savedKey.split('\n') : []);
  }, [savedKey]);
  const dirty = !sameList(draft, user.aliases);

  const add = () => {
    const alias = input.trim();
    if (!alias) return;
    if (alias.length > MAX_ALIAS_LENGTH) {
      setError(t('An alias can be at most {{max}} characters.', { max: MAX_ALIAS_LENGTH }));
      return;
    }
    if (!draft.includes(alias)) {
      if (draft.length >= MAX_ALIASES) {
        setError(t('A user can have at most {{max}} aliases.', { max: MAX_ALIASES }));
        return;
      }
      setDraft([...draft, alias]);
    }
    setInput('');
    setError(null);
  };

  const save = async () => {
    if (!client) return;
    setSaving(true);
    setError(null);
    try {
      const result = await client.setAliases(user.user_id, draft);
      onSaved(user.user_id, Array.isArray(result?.aliases) ? result.aliases : draft);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const who = user.name || user.email || user.user_id;
  return (
    <tr className="border-t border-default-100 align-top" aria-label={who}>
      <td className="py-2 pr-4">
        <div className="flex flex-col">
          <span className="text-sm text-foreground">{who}</span>
          {user.email && user.email !== who ? <span className="text-xs text-default-500">{user.email}</span> : null}
        </div>
      </td>
      <td className="py-2">
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-1">
            {draft.length === 0 && <span className="text-xs text-default-400">{t('No aliases')}</span>}
            {draft.map((alias) => (
              <Chip
                key={alias}
                size="sm"
                variant="flat"
                endContent={saving ? undefined : (
                  <button
                    type="button"
                    aria-label={t('Remove alias {{alias}}', { alias })}
                    className="mx-1 flex items-center text-default-500 hover:text-danger"
                    onClick={() => setDraft(draft.filter((a) => a !== alias))}
                  >
                    <Icon icon="lucide:x" width={12} />
                  </button>
                )}
              >
                {alias}
              </Chip>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              aria-label={t('New alias for {{name}}', { name: who })}
              placeholder={t('Add an alias')}
              size="sm"
              className="max-w-[220px]"
              value={input}
              onValueChange={setInput}
              isDisabled={saving}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  add();
                }
              }}
            />
            <CtyButton variant="flat" size="sm" onPress={add} isDisabled={saving || !input.trim()}>
              {t('Add')}
            </CtyButton>
            <CtyButton color="primary" size="sm" onPress={() => void save()} isDisabled={!dirty || saving} isLoading={saving}>
              {t('Save')}
            </CtyButton>
            {dirty && !saving && (
              <CtyButton variant="light" size="sm" onPress={() => { setDraft(user.aliases); setError(null); }}>
                {t('Discard')}
              </CtyButton>
            )}
          </div>
          <ErrorLine message={error} />
        </div>
      </td>
    </tr>
  );
}

export default AccessMatrixTab;
